import React, { FunctionComponent, useState, useRef, useEffect } from 'react'
import { notification, Card, List, Typography, Progress, Alert, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useParams } from 'react-router'
import { ScreenersService } from '../api/services.gen'

interface TemporaryResult {
  root: {
    id: string
    symbol: string
    currency: string
    [key: string]: any
  }
  parameters: {
    symbol: string
    rootSymbol: string
    type: string
    strikePrice: number
    expiration: string
    [key: string]: any
  }
  option: {
    symbol: string
  }
  optionPrice: {
    bid: number
    ask: number
    volume: number
    date: string
    source: string
    realtime: boolean
  }
  rootPrice: {
    bid: number
    ask: number
    volume: number
    date: string
    source: string
    realtime: boolean
  }
  perDay: number
  ratio: number
  perDayTotal: number
  perDayPercent: number | null
  [key: string]: any
}

interface ScreenerMessage {
  type: string
  message: string
  data?: any
  [key: string]: any
}

const OptionInterestScreenerRunPage: FunctionComponent = () => {
  const { runId } = useParams<{ runId: string }>()
  const [results, setResults] = useState<TemporaryResult[]>([])
  const [logs, setLogs] = useState<string[]>([])
  const [progress, setProgress] = useState<number>(0)
  const [error, setError] = useState<string | null>(null)
  const logsContainerRef = useRef<HTMLDivElement>(null)
  const eventSourceRef = useRef<EventSource | null>(null)

  useEffect(() => {
    // Auto scroll to bottom within the logs container
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight
    }
  }, [logs])

  useEffect(() => {
    if (!runId) {
      notification.error({
        message: 'Error',
        description: 'No run ID provided'
      })
      return
    }

    const validateAndStartStream = async () => {
      try {
        // Validate that the run exists and is for option interest screener
        const runStatus = await ScreenersService.getRunStatus({ runId })
        
        if (runStatus.screenerName !== 'optionInterest') {
          setError(`This run is for "${runStatus.screenerName}" screener, not "optionInterest"`)
          return
        }

        const server = localStorage.getItem('auto_trading_server') || 'https://stonks.florianhj.fr/api'
        const url = `${server}/v1/screeners/runs/run/${runId}/events`
        
        // Use EventSource for SSE
        const eventSource = new EventSource(url)
        eventSourceRef.current = eventSource
        
        eventSource.onmessage = (event) => {
          try {
            const data: ScreenerMessage = JSON.parse(event.data)
            
            if (data.type === 'log') {
              setLogs((prev) => [...prev, data.message])
            } else if (data.type === 'progress') {
              setProgress(data.data * 100)
            } else if (data.type === 'temporaryResult' || data.type === 'result') {
              setResults((prev) => [...prev, data.data])
            } else if (data.type === 'complete') {
              setResults(data.data)
            }
          } catch (e) {
            console.error('Failed to parse SSE data:', e)
          }
        }
        
        eventSource.onerror = (error) => {
          console.error('SSE Error:', error)
          eventSource.close()
          
          notification.error({
            message: 'Screener error',
            description: 'Failed to receive results from screener'
          })
        }
        
        eventSource.addEventListener('done', () => {
          eventSource.close()
          notification.success({
            message: 'Screener completed',
          })
        })
      } catch (e: any) {
        console.error('Failed to validate run:', e)
        setError(e.message || 'Failed to load screener run')
      }
    }

    validateAndStartStream()

    // Cleanup on unmount
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
      }
    }
  }, [runId])

  const bestResult = results.reduce<TemporaryResult | null>((best, current) => {
    if (best === null) return current
    if ((current.perDayPercent ?? -Infinity) > (best.perDayPercent ?? -Infinity)) return current
    return best
  }, null)

  const columns: ColumnsType<TemporaryResult> = [
    {
      title: 'Option',
      dataIndex: ['parameters', 'symbol'],
      key: 'symbol',
    },
    {
      title: 'Root',
      key: 'root',
      render: (_, record) => `${record.root?.symbol} (${record.root?.currency})`,
    },
    {
      title: 'Strike',
      dataIndex: ['parameters', 'strikePrice'],
      key: 'strikePrice',
    },
    {
      title: 'Expiration',
      key: 'expiration',
      render: (_, record) =>
        record.parameters?.expiration
          ? new Date(record.parameters.expiration).toLocaleDateString()
          : '',
    },
    {
      title: 'Option Bid/Ask',
      key: 'optionPrice',
      render: (_, record) => `${record.optionPrice?.bid} / ${record.optionPrice?.ask}`,
    },
    {
      title: 'Root Bid/Ask',
      key: 'rootPrice',
      render: (_, record) => `${record.rootPrice?.bid} / ${record.rootPrice?.ask}`,
    },
    {
      title: 'Ratio',
      dataIndex: 'ratio',
      key: 'ratio',
    },
    {
      title: '$/day',
      key: 'perDayTotal',
      render: (_, record) => record.perDayTotal?.toFixed(2) ?? record.perDay?.toFixed(2),
      sorter: (a, b) => (a.perDayTotal ?? a.perDay ?? 0) - (b.perDayTotal ?? b.perDay ?? 0),
    },
    {
      title: '% per day',
      key: 'perDayPercent',
      defaultSortOrder: 'descend',
      render: (_, record) =>
        record.perDayPercent !== null && record.perDayPercent !== undefined
          ? `${(record.perDayPercent * 100).toFixed(3)}%`
          : 'N/A',
      sorter: (a, b) => (a.perDayPercent ?? -Infinity) - (b.perDayPercent ?? -Infinity),
    },
  ]

  return (
    <div>
      <h2>Screener Run: {runId}</h2>
      
      {error && (
        <Alert
          message="Invalid Screener Type"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      
      <Card title="Progress" style={{ marginBottom: 16 }}>
        <Progress percent={Math.round(progress)} status={progress === 100 ? 'success' : 'active'} />
      </Card>

      <Card title="Best Option" style={{ marginBottom: 16 }}>
        {bestResult ? (
          <>
            <div style={{ fontSize: '24px', marginBottom: '16px', color: '#52c41a', fontWeight: 'bold' }}>
              {bestResult.perDayPercent !== null 
                ? `${(bestResult.perDayPercent * 100).toFixed(2)}% per day ($${bestResult.perDay?.toFixed(2)}/day)` 
                : 'N/A'}
            </div>
            <List size="small">
              <List.Item>
                <Typography.Text strong>Option:</Typography.Text> {bestResult.parameters?.symbol}
              </List.Item>
              <List.Item>
                <Typography.Text strong>Root:</Typography.Text> {bestResult.root?.symbol} ({bestResult.root?.currency})
              </List.Item>
              <List.Item>
                <Typography.Text strong>Strike Price:</Typography.Text> {bestResult.parameters?.strikePrice}
              </List.Item>
              <List.Item>
                <Typography.Text strong>Expiration:</Typography.Text> {bestResult.parameters?.expiration && new Date(bestResult.parameters.expiration).toLocaleDateString()}
              </List.Item>
              <List.Item>
                <Typography.Text strong>Option Bid/Ask:</Typography.Text> {bestResult.optionPrice?.bid} / {bestResult.optionPrice?.ask} (vol: {bestResult.optionPrice?.volume?.toLocaleString()})
              </List.Item>
              <List.Item>
                <Typography.Text strong>Root Price:</Typography.Text> {bestResult.rootPrice?.bid} / {bestResult.rootPrice?.ask} (vol: {bestResult.rootPrice?.volume?.toLocaleString()})
              </List.Item>
              <List.Item>
                <Typography.Text strong>Ratio:</Typography.Text> {bestResult.ratio?.toFixed(4)}
              </List.Item>
              <List.Item>
                <Typography.Text strong>$/day (total):</Typography.Text> {bestResult.perDayTotal?.toFixed(2) ?? bestResult.perDay?.toFixed(2)}
              </List.Item>
            </List>
          </>
        ) : (
          <Typography.Text type="secondary">No result yet...</Typography.Text>
        )}
      </Card>

      <Card title="Logs">
        <div 
          ref={logsContainerRef}
          style={{ 
            maxHeight: '400px', 
            overflowY: 'auto', 
            backgroundColor: '#f5f5f5', 
            padding: '8px',
            fontFamily: 'monospace',
            fontSize: '12px',
            whiteSpace: 'pre'
          }}
        >
          {logs.map((log, index) => (
            <div key={index}>{log}</div>
          ))}
        </div>
      </Card>

      <Card title={`All Results (${results.length})`} style={{ marginTop: 16 }}>
        <Table
          columns={columns}
          dataSource={results}
          rowKey={(record) => record.parameters?.symbol}
          size="small"
          pagination={false}
          scroll={{ x: true }}
        />
      </Card>
    </div>
  )
}

export default OptionInterestScreenerRunPage
